/**
 * Months Vocabulary Data
 * 12 months of the year with American and British pronunciation
 */

import { VocabularyWord } from './vocabulary';

export const monthsVocabulary: VocabularyWord[] = [
  {
    id: 'january',
    word: 'January',
    phonetic: '/ˈdʒænjueri/',
    phoneticUS: '/ˈdʒænjueri/',
    phoneticUK: '/ˈdʒænjuəri/',
    meaning: '一月',
    example: 'January is the first month of the year.',
    exampleTranslation: '一月是一年中的第一个月。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '📅'
  },
  {
    id: 'february',
    word: 'February',
    phonetic: '/ˈfebrueri/',
    phoneticUS: '/ˈfebrueri/',
    phoneticUK: '/ˈfebruəri/',
    meaning: '二月',
    example: 'Chinese New Year is usually in February.',
    exampleTranslation: '中国新年通常在二月。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '🧧'
  },
  {
    id: 'march',
    word: 'March',
    phonetic: '/mɑrtʃ/',
    phoneticUS: '/mɑrtʃ/',
    phoneticUK: '/mɑːtʃ/',
    meaning: '三月',
    example: 'March is in spring.',
    exampleTranslation: '三月是春天。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '🌸'
  },
  {
    id: 'april',
    word: 'April',
    phonetic: '/ˈeɪprəl/',
    phoneticUS: '/ˈeɪprəl/',
    phoneticUK: '/ˈeɪprəl/',
    meaning: '四月',
    example: 'April showers bring May flowers.',
    exampleTranslation: '四月雨带来五月花。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '🌷'
  },
  {
    id: 'may',
    word: 'May',
    phonetic: '/meɪ/',
    phoneticUS: '/meɪ/',
    phoneticUK: '/meɪ/',
    meaning: '五月',
    example: 'May is a beautiful month.',
    exampleTranslation: '五月是一个美丽的月份。',
    category: 'months',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    image: '🌺'
  },
  {
    id: 'june',
    word: 'June',
    phonetic: '/dʒuːn/',
    phoneticUS: '/dʒuːn/',
    phoneticUK: '/dʒuːn/',
    meaning: '六月',
    example: "Children's Day is in June.",
    exampleTranslation: '儿童节在六月。',
    category: 'months',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    image: '🎈'
  },
  {
    id: 'july',
    word: 'July',
    phonetic: '/dʒuˈlaɪ/',
    phoneticUS: '/dʒuˈlaɪ/',
    phoneticUK: '/dʒuːˈlaɪ/',
    meaning: '七月',
    example: 'Summer vacation starts in July.',
    exampleTranslation: '暑假从七月开始。',
    category: 'months',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    image: '☀️'
  },
  {
    id: 'august',
    word: 'August',
    phonetic: '/ˈɔɡəst/',
    phoneticUS: '/ˈɔɡəst/',
    phoneticUK: '/ˈɔːɡəst/',
    meaning: '八月',
    example: 'August is very hot.',
    exampleTranslation: '八月很热。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '🏖️'
  },
  {
    id: 'september',
    word: 'September',
    phonetic: '/sepˈtembər/',
    phoneticUS: '/sepˈtembər/',
    phoneticUK: '/sepˈtembə/',
    meaning: '九月',
    example: 'School starts in September.',
    exampleTranslation: '学校在九月开学。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '📚'
  },
  {
    id: 'october',
    word: 'October',
    phonetic: '/ɑkˈtoʊbər/',
    phoneticUS: '/ɑkˈtoʊbər/',
    phoneticUK: '/ɒkˈtəʊbə/',
    meaning: '十月',
    example: 'October has golden leaves.',
    exampleTranslation: '十月有金色的树叶。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '🍂'
  },
  {
    id: 'november',
    word: 'November',
    phonetic: '/noʊˈvembər/',
    phoneticUS: '/noʊˈvembər/',
    phoneticUK: '/nəʊˈvembə/',
    meaning: '十一月',
    example: 'November is getting cold.',
    exampleTranslation: '十一月变冷了。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '🌬️'
  },
  {
    id: 'december',
    word: 'December',
    phonetic: '/dɪˈsembər/',
    phoneticUS: '/dɪˈsembər/',
    phoneticUK: '/dɪˈsembə/',
    meaning: '十二月',
    example: 'Christmas is in December.',
    exampleTranslation: '圣诞节在十二月。',
    category: 'months',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    image: '🎄'
  }
];

/**
 * Get months vocabulary for elementary school
 * Suitable for grade 5-6
 */
export function getMonthsVocabulary(): VocabularyWord[] {
  return monthsVocabulary;
}

/**
 * Get month by name (case-insensitive)
 */
export function getMonthByName(name: string): VocabularyWord | undefined {
  return monthsVocabulary.find(
    m => m.word.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Get month by number (1-12)
 */
export function getMonthByNumber(num: number): VocabularyWord | undefined {
  const months = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];
  const monthName = months[num - 1];
  return monthsVocabulary.find(m => m.id === monthName);
}
